import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";

/**
 * Wrapper inteligente sobre ApexCharts com defaults pensados pro /reports.
 *
 * Resolve:
 *  - X-axis estourando com muitos pontos → labels rotacionadas + hideOverlapping
 *  - Charts esticados em mobile → altura adaptativa
 *  - Faixa de cores consistente entre blocos
 *
 * Props:
 *  - type:       'line' | 'area' | 'bar' | 'donut'
 *  - categories: array (line/bar/area)
 *  - series:     [{name, data}] (line/bar/area) OU [number] (donut)
 *  - labels:     array (donut)
 *  - colors:     opcional, override de paleta
 *  - height:     opcional, override
 *  - extra:      objeto de options pra mergear (por bloco)
 *  - emptyText:  texto exibido quando não há dado
 */
const isMobile = () =>
  typeof window !== "undefined" && window.innerWidth <= 768;

const baseOptions = {
  chart: {
    toolbar: { show: false },
    zoom: { enabled: false },
    animations: { speed: 400 },
    fontFamily: "inherit",
  },
  grid: { borderColor: "#f1f5f9", strokeDashArray: 2 },
  legend: {
    position: "top",
    horizontalAlign: "left",
    fontSize: "11px",
    fontWeight: 500,
    markers: { width: 8, height: 8 },
  },
  tooltip: { theme: "light", style: { fontSize: "12px" } },
};

const buildXAxisLabels = (categories) => {
  const total = categories?.length || 0;

  // Sem dados — sai
  if (!total) return { style: { fontSize: "11px", colors: ["#64748b"] } };

  // Com muitos pontos: rotaciona, esconde sobrepostos, e usa formatter
  // que mostra ~10-14 labels distribuídos uniformemente.
  if (total > 14) {
    const step = Math.ceil(total / (isMobile() ? 6 : 12));
    return {
      style: { fontSize: "10px", colors: ["#64748b"] },
      rotate: -45,
      rotateAlways: true,
      hideOverlappingLabels: true,
      trim: false,
      formatter: (val, _ts, opts) => {
        const idx = opts?.dataPointIndex;
        if (idx == null) return val;
        // Garante que primeiro e último apareçam
        if (idx === 0 || idx === total - 1) return val;
        return idx % step === 0 ? val : "";
      },
    };
  }

  // Poucos pontos: rotação leve só pra evitar acidente de overlap
  return {
    style: { fontSize: "11px", colors: ["#64748b"] },
    rotate: total > 8 ? -30 : 0,
    rotateAlways: false,
    hideOverlappingLabels: true,
    trim: true,
  };
};

const InsightsChart = ({
  type,
  categories = [],
  series = [],
  labels = [],
  colors,
  height,
  extra = {},
  emptyText = "Sem dados no período.",
}) => {
  const computedHeight = useMemo(() => {
    if (height) return height;
    const mobile = isMobile();
    if (type === "donut") return mobile ? 240 : 280;
    return mobile ? 220 : 300;
  }, [type, height]);

  const isEmpty = useMemo(() => {
    if (type === "donut") return !series.length;
    if (!categories.length || !series.length) return true;
    return series.every((s) => !(s.data || []).length);
  }, [type, categories, series]);

  const options = useMemo(() => {
    if (type === "donut") {
      return {
        ...baseOptions,
        chart: { ...baseOptions.chart, type: "donut" },
        colors: colors || ["#2bc155", "#137ff7", "#f59e0b", "#a855f7", "#ef4444", "#06b6d4", "#84cc16", "#f97316"],
        labels,
        legend: { ...baseOptions.legend, position: "bottom" },
        plotOptions: {
          pie: {
            donut: {
              size: "60%",
              labels: {
                show: true,
                total: { show: true, fontSize: "13px", fontWeight: 600, color: "#0f172a" },
              },
            },
          },
        },
        dataLabels: { enabled: true, style: { fontSize: "10px", colors: ["#fff"] } },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: { height: 220 },
              legend: { position: "bottom", fontSize: "10px" },
            },
          },
        ],
        ...extra,
      };
    }

    // line / bar / area — monta sem chaves undefined (ApexCharts quebra com `markers: undefined`)
    const opts = {
      ...baseOptions,
      chart: { ...baseOptions.chart, type },
      colors: colors || ["#137ff7", "#16a34a", "#f59e0b", "#a855f7"],
      stroke: { curve: "smooth", width: type === "bar" ? 0 : 3 },
      dataLabels: { enabled: false },
      xaxis: {
        categories,
        labels: buildXAxisLabels(categories),
        axisTicks: { show: false },
        axisBorder: { show: false },
      },
      yaxis: {
        labels: {
          style: { fontSize: "11px", colors: ["#64748b"] },
          formatter: (v) => {
            if (typeof v !== "number") return v;
            if (Math.abs(v) >= 1000) return (v / 1000).toFixed(1) + "k";
            return Math.round(v);
          },
        },
      },
    };

    if (type === "line") {
      opts.markers = {
        size: categories.length > 30 ? 0 : 4,
        hover: { size: 6 },
      };
    }
    if (type === "area") {
      opts.fill = {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.3,
          opacityTo: 0.05,
          stops: [0, 100],
        },
      };
    }

    return { ...opts, ...extra };
  }, [type, categories, labels, colors, extra]);

  if (isEmpty) {
    return (
      <div className="insights-chart--empty">
        <p>{emptyText}</p>
      </div>
    );
  }

  return (
    <ReactApexChart
      type={type}
      height={computedHeight}
      series={series}
      options={options}
    />
  );
};

export default InsightsChart;
